import { useRef, useMemo, useState, useCallback } from "react";
import * as Popover from "@radix-ui/react-popover";
import { CaretDownIcon } from "@phosphor-icons/react";
import { PhosphorIcon } from "../phosphorIcon/PhosphorIcon";

export const SEARCHABLE_FILTER_ALL_VALUE = "__ALL__";

const TRIGGER_BASE =
    "h-[40px] rounded-pill border bg-white font-primary text-m text-p6-grey-dark px-s flex items-center justify-between gap-xs outline-none w-full hover:border-p6-orange cursor-pointer";
const TRIGGER_DISABLED_CLASSES =
    "h-[40px] rounded-pill border border-p6-grey-mid bg-white font-primary text-m text-p6-grey-mid px-s flex items-center justify-between gap-xs outline-none w-full cursor-not-allowed";
const CONTENT_CLASSES =
    "rounded-[12px] bg-white border border-p6-grey-lightest shadow-[0_2px_8px_rgba(0,0,0,0.12)] overflow-hidden z-50";

const ITEM_CLASSES =
    "font-primary text-m text-p6-grey-dark min-h-[40px] leading-[1.5] py-xs px-s cursor-pointer outline-none hover:bg-p6-orange/25 list-none";

interface SearchableFilterProps {
    options: Array<{ name: string; value: string }>;
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    "aria-labelledby"?: string;
}

const getOptionId = (index: number) => `searchable-filter-option-${index}`;

const LISTBOX_ID = "searchable-filter-listbox";

const SearchableFilter = (props: SearchableFilterProps) => {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);

    const disabled = props.options.length === 0;
    const onChange = props.onChange;

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return props.options;
        const lower = searchTerm.toLowerCase();
        return props.options.filter((opt) => opt.name.toLowerCase().includes(lower));
    }, [props.options, searchTerm]);

    const selectedLabel = useMemo(() => {
        if (props.value === "") return "";
        const found = props.options.find((opt) => opt.value === props.value);
        return found ? found.name : props.value;
    }, [props.value, props.options]);

    const handleSelect = useCallback(
        (value: string) => {
            onChange(value === SEARCHABLE_FILTER_ALL_VALUE ? "" : value);
            setSearchTerm("");
            setHighlightedIndex(-1);
            setOpen(false);
        },
        [onChange]
    );

    const handleOpenChange = useCallback((nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            setSearchTerm("");
            setHighlightedIndex(-1);
        }
    }, []);

    const handleTriggerClick = useCallback(() => {
        inputRef.current?.focus();
        setOpen(true);
    }, []);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!open) return;
            const count = filteredOptions.length;
            if (count === 0) return;
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightedIndex((prev) => (prev + 1) % count);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightedIndex((prev) => (prev - 1 + count) % count);
            } else if (e.key === "Enter" && highlightedIndex >= 0 && highlightedIndex < count) {
                e.preventDefault();
                handleSelect(filteredOptions[highlightedIndex].value);
            } else if (e.key === "Escape") {
                e.preventDefault();
                setOpen(false);
                setSearchTerm("");
                setHighlightedIndex(-1);
            }
        },
        [open, filteredOptions, highlightedIndex, handleSelect]
    );

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchTerm(e.target.value);
            setHighlightedIndex(-1);
            if (!open) setOpen(true);
        },
        [open]
    );

    if (disabled) {
        return (
            <div className={TRIGGER_DISABLED_CLASSES}>
                <input
                    className="flex-1 bg-transparent outline-none font-primary text-m text-p6-grey-mid placeholder:text-p6-grey-mid min-w-0 cursor-not-allowed"
                    placeholder={props.placeholder}
                    disabled
                    aria-labelledby={props["aria-labelledby"]}
                />
                <PhosphorIcon
                    phosphorIcon={CaretDownIcon}
                    color="text-p6-grey-mid"
                    fontSize="text-m"
                />
            </div>
        );
    }

    return (
        <Popover.Root
            open={open}
            onOpenChange={handleOpenChange}
        >
            <Popover.Anchor asChild>
                <div
                    className={`${TRIGGER_BASE} ${open ? "border-p6-orange" : "border-p6-grey-mid"}`}
                    onClick={handleTriggerClick}
                >
                    <input
                        ref={inputRef}
                        className="flex-1 bg-transparent outline-none font-primary text-m text-p6-grey-dark placeholder:text-p6-grey-mid min-w-0"
                        placeholder={props.placeholder}
                        aria-labelledby={props["aria-labelledby"]}
                        role="combobox"
                        aria-expanded={open}
                        aria-controls={open ? LISTBOX_ID : undefined}
                        aria-haspopup="listbox"
                        aria-activedescendant={highlightedIndex >= 0 ? getOptionId(highlightedIndex) : undefined}
                        value={open ? searchTerm : selectedLabel}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                    />
                    <PhosphorIcon
                        phosphorIcon={CaretDownIcon}
                        color="text-p6-grey-dark"
                        fontSize="text-m"
                    />
                </div>
            </Popover.Anchor>
            {filteredOptions.length > 0 && (
                <Popover.Portal>
                    <Popover.Content
                        sideOffset={8}
                        className={CONTENT_CLASSES}
                        style={{ minWidth: "var(--radix-popover-trigger-width)" }}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                        <ul
                            id={LISTBOX_ID}
                            role="listbox"
                            className="max-h-[300px] overflow-y-auto"
                        >
                            {filteredOptions.map((opt, index) => {
                                const isSelected = opt.value === (props.value || SEARCHABLE_FILTER_ALL_VALUE);
                                const isHighlighted = index === highlightedIndex;
                                return (
                                    <li
                                        key={opt.value}
                                        id={getOptionId(index)}
                                        role="option"
                                        aria-selected={isSelected}
                                        className={`${ITEM_CLASSES} ${
                                            isSelected
                                                ? "bg-p6-orange text-white"
                                                : isHighlighted
                                                ? "bg-p6-orange/25"
                                                : ""
                                        }`}
                                        onClick={() => handleSelect(opt.value)}
                                    >
                                        {opt.name}
                                    </li>
                                );
                            })}
                        </ul>
                    </Popover.Content>
                </Popover.Portal>
            )}
        </Popover.Root>
    );
};

export default SearchableFilter;
